import { api, apiClientV2, unwrapV2Response } from './index.js';

/**
 * Fetches the model grid for a project from the v2 grids endpoint.
 *
 * @async
 * @param {Object} params
 * @param {string} params.project_uuid - The UUID of the project.
 * @param {string} params.model_uuid - The UUID of the model.
 * @returns {Promise<{grid_geojson: Object, grid_size: number}>} The grid data.
 */
export const getGrid = async ({ project_uuid, model_uuid }) => {
	const { data, error } = await apiClientV2.get('/grids', { project_uuid, model_uuid });

	if (error) {
		throw new Error(error.message || 'Failed to fetch grid');
	}

	return unwrapV2Response(data);
};

/**
 * Returns the model grid tiles as GeoJSON features, or an empty list when the grid cannot be loaded.
 */
export const getGridFeatures = async ({ project_uuid, model_uuid }) => {
	try {
		const { grid_geojson } = await getGrid({ project_uuid, model_uuid });
		return grid_geojson?.features ?? [];
	} catch (error) {
		return [];
	}
};

export const getUnrotatedGrid = async projectId => {
	const { data, error } = await apiClientV2.get(`/grids/unrotated?project_uuid=${projectId}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

export const getRotatedGrid = async projectId => {
	const { data, error } = await apiClientV2.get(`/grids/rotated?project_uuid=${projectId}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Adds an annotation to the grid for a specific project, model and file
 *
 * @async
 * @param {Object} params - The parameters for adding an annotation.
 * @param {string} params.projectUuid - The UUID of the project.
 * @param {string} params.filename - The name of the file to add the annotation.
 * @param {string} params.classname - The class name for the annotation.
 * @param {string} params.classid - The class ID for the annotation.
 * @param {string} params.modelUuid - The UUID of the model.
 * @param {Array|Object} params.polygon - The normalized polygon data for the annotation.
 * @returns {Promise<Object>} The data returned from the API.
 */
export const addAnnotationToGrid = async ({
	projectUuid,
	filename,
	classname,
	classid,
	modelUuid,
	polygon,
}) => {
	const { data } = await api.post(`/grid/${projectUuid}/point_px`, {
		filename,
		classname,
		classid,
		model_uuid: modelUuid,
		polygon,
	});

	return data;
};

/**
 * Deletes an annotation from the grid for a specific project and model
 *
 * @async
 * @param {Object} params - The parameters for deleting an annotation
 * @param {string} params.projectUuid - UUID of the project
 * @param {string} params.modelUuid - UUID of the model
 * @param {string} params.annotationUuid - UUID of the annotation to be deleted
 * @returns {Promise<Object>} The data returned from the API.
 */
export const deleteAnnotationFromGrid = async ({ projectUuid, modelUuid, annotationUuid }) => {
	const { data } = await api.delete(`/grid/${projectUuid}/annotation`, {
		data: {
			model_uuid: modelUuid,
			annotation_uuid: annotationUuid,
		},
	});
	return data;
};
